// src/utils/NumericValue.ts

/**
 * Turns a value that should be a number into one, whatever the service actually sent.
 *
 * Feature services in this app routinely hand back numbers as text — `"1,234"`, `"$500"`,
 * `"12%"`, `" 7 "`, `""` for null. A string reaching a `number` column poisons everything
 * downstream at once: `sum` skips it (the aggregators all guard on `typeof === 'number'`),
 * sorting falls back to `localeCompare` so 100 lands before 9, the number filter operators
 * compare against text, and charts plot it as a category instead of a measure.
 *
 * Anything that can't be read as a finite number becomes `null`, which is what the grid,
 * the aggregators and the formatters already treat as "no value" — never `NaN`.
 *
 * Lives here rather than beside any one grid: the feature service data source needs it for
 * raw attributes, and Summary Reports needs it for its number columns.
 */
export const coerceNumericValue = (value: unknown): number | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    if (trimmed === '') {
        return null;
    }

    // The shapes a service actually emits for a number-as-text: parenthesised negatives
    // first, then thousands separators, currency and a trailing percent.
    const normalised = trimmed.replace(/^\((.*)\)$/, '-$1').replace(/[$,\s%]/g, '');

    const parsed = Number(normalised);
    return Number.isFinite(parsed) ? parsed : null;
};

